/**
 * MemorialsPage — manage memorial pages across the archive.
 * Route: /memorials
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Copy,
  ExternalLink,
  Flame,
  Loader2,
  QrCode,
  Frame,
  Eye,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArchiveEmptyState } from '../components/archive/ArchiveEmptyState';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { ThemeToggle } from '../components/ThemeToggle';
import AppFooter from '../components/AppFooter';
import { getApiBase } from '../utils/apiBase';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { getBackendBaseUrl } from '../utils/apiBase';
import {
  formatPersonDates,
  getMemorialPublicUrl,
  getMemorialStatusLabel,
  getMemorialVisibilityLabel,
  isMemorialPublished,
  memorialManageHref,
  personDisplayName,
} from '../utils/memorial';
import {
  MEMORIAL_BRIDGE_DISCLAIMER,
  MEMORIAL_BRIDGE_PILLAR_LABEL,
  MEMORIAL_BRIDGE_SAFETY,
  MEMORIAL_BRIDGE_TAGLINE,
} from '../content/memorialBridgeContent';
import { MEMORIAL_KEEPSAKES_HUB_COPY, MEMORIAL_PHOTO_LINK_NAME } from '../content/memorialKeepsakesContent';
import { personHref } from '../utils/prettyArchiveLinks';

const API = getApiBase();
const BACKEND = getBackendBaseUrl();

function MemorialCard({ item, variant = 'default' }) {
  const person = item.person;
  const memorial = item.memorial;
  const name = personDisplayName(person);
  const dates = formatPersonDates(person);
  const published = isMemorialPublished(memorial);
  const publicUrl = getMemorialPublicUrl(item.public_url, memorial?.slug);
  const status = getMemorialStatusLabel(memorial);
  const visibility = getMemorialVisibilityLabel(memorial, published);
  const photo = person?.profile_image_url ? resolveAssetUrl(BACKEND, person.profile_image_url) : null;

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Memorial link copied.');
    } catch {
      toast.error('Could not copy link.');
    }
  };

  return (
    <Card className="overflow-hidden border-[hsl(38_14%_88%)]/70 bg-[hsl(42_28%_99%)]" data-testid={`memorial-card-${person?.id}`}>
      <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start">
        {photo ? (
          <img src={photo} alt="" className="h-16 w-16 shrink-0 rounded-full border border-border/40 object-cover" />
        ) : (
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-muted/40 text-muted-foreground">
            <Flame className="h-6 w-6 opacity-60" />
          </span>
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <p className="font-serif text-lg text-foreground">{name}</p>
            {dates ? <p className="text-xs tabular-nums text-muted-foreground">{dates}</p> : null}
          </div>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="rounded-full bg-muted/50 px-2 py-0.5 text-muted-foreground">{status}</span>
            <span className="rounded-full bg-muted/50 px-2 py-0.5 text-muted-foreground">{visibility}</span>
            {item.tribute_count > 0 ? (
              <span className="rounded-full bg-muted/50 px-2 py-0.5 text-muted-foreground">
                {item.tribute_count} tribute{item.tribute_count === 1 ? '' : 's'}
              </span>
            ) : null}
            {item.photo_count > 0 ? (
              <span className="rounded-full bg-muted/50 px-2 py-0.5 text-muted-foreground">
                {item.photo_count} photo{item.photo_count === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
          {published && publicUrl ? (
            <p className="truncate text-xs text-muted-foreground">{publicUrl}</p>
          ) : null}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="button" size="sm" className="rounded-full" asChild>
              <Link to={memorialManageHref(person)}>
                Open workspace
                <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
            {published && publicUrl ? (
              <>
                <Button type="button" size="sm" variant="outline" className="rounded-full" asChild>
                  <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                    <Eye className="mr-1 h-3 w-3" />
                    Preview
                  </a>
                </Button>
                <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={copyLink}>
                  <Copy className="mr-1 h-3 w-3" />
                  Copy link
                </Button>
                <Button type="button" size="sm" variant="outline" className="rounded-full" asChild>
                  <Link to={memorialManageHref(person)}>
                    <QrCode className="mr-1 h-3 w-3" />
                    View QR
                  </Link>
                </Button>
              </>
            ) : variant === 'eligible' ? (
              <Button type="button" size="sm" variant="outline" className="rounded-full" asChild>
                <Link to={memorialManageHref(person)}>Set up memorial</Link>
              </Button>
            ) : null}
            {person ? (
              <Button type="button" size="sm" variant="ghost" className="rounded-full" asChild>
                <Link to={personHref(person)}>Person profile</Link>
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Section({ id, title, description, children, empty, emptyIcon: EmptyIcon, emptyAction }) {
  return (
    <section id={id} className={id ? 'scroll-mt-24 space-y-4' : 'space-y-4'}>
      <div>
        <h2 className="font-serif text-xl font-light text-foreground">{title}</h2>
        {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {empty ? (
        <ArchiveEmptyState
          icon={EmptyIcon || Flame}
          align="start"
          title={empty.title}
          description={empty.description}
          actions={emptyAction}
        />
      ) : (
        children
      )}
    </section>
  );
}

export default function MemorialsPage() {
  const [searchParams] = useSearchParams();
  const treeFilter = searchParams.get('tree');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [eligible, setEligible] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = treeFilter ? `?tree_id=${encodeURIComponent(treeFilter)}` : '';
      const { data } = await axios.get(`${API}/memorials-api${qs}`);
      setItems(data.items || []);
      setEligible(data.eligible || []);
    } catch {
      toast.error('Could not load memorials.');
      setItems([]);
      setEligible([]);
    } finally {
      setLoading(false);
    }
  }, [treeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const drafts = useMemo(
    () => items.filter((i) => i.memorial?.status === 'draft'),
    [items]
  );
  const published = useMemo(
    () => items.filter((i) => isMemorialPublished(i.memorial)),
    [items]
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border/40 bg-[hsl(42_28%_99%)]/95">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/dashboard" className="shrink-0">
            <BrandWordmark className="h-7" />
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 sm:py-10" data-testid="memorials-hub-page">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">
              <Flame className="h-3.5 w-3.5" aria-hidden />
              {MEMORIAL_BRIDGE_PILLAR_LABEL}
            </p>
            <h1 className="mt-2 font-serif text-3xl font-light text-foreground">Memorial Bridge</h1>
            <p className="mt-3 text-[0.95rem] leading-relaxed text-muted-foreground">{MEMORIAL_BRIDGE_TAGLINE}</p>
            <p className="mt-2 text-xs text-muted-foreground/90">{MEMORIAL_BRIDGE_SAFETY}</p>
          </div>
          <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-full" asChild>
            <Link to="/guide#memorial-bridge">Feature Guide</Link>
          </Button>
        </div>

        <div
          className="mb-8 rounded-xl border border-[hsl(38_16%_82%)] bg-white/70 px-4 py-3 text-sm leading-relaxed text-[#5c534a]"
          role="note"
          data-testid="memorial-bridge-disclaimer"
        >
          <strong className="font-medium text-[#1c1917]">Please note:</strong> {MEMORIAL_BRIDGE_DISCLAIMER}
        </div>

        {!loading ? (
          <div className="mb-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="memorials-hub-summary">
            <div className="rounded-xl border border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)] px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8b7355]">{published.length}</p>
              <p className="mt-1 text-sm text-[#5c534a]">Published memorials</p>
            </div>
            <div className="rounded-xl border border-[hsl(38_16%_82%)] bg-white/60 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8b7355]">{drafts.length}</p>
              <p className="mt-1 text-sm text-[#5c534a]">Private drafts</p>
            </div>
            <div className="rounded-xl border border-[hsl(38_16%_82%)] bg-white/60 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8b7355]">{eligible.length}</p>
              <p className="mt-1 text-sm text-[#5c534a]">Ready to set up</p>
            </div>
            <div className="rounded-xl border border-[hsl(38_16%_82%)] bg-white/60 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8b7355]">QR</p>
              <p className="mt-1 text-sm text-[#5c534a]">Available after publishing</p>
            </div>
          </div>
        ) : null}

        {!loading ? (
          <div
            className="mb-10 rounded-2xl border border-[hsl(38_16%_82%)] bg-gradient-to-br from-[hsl(42_28%_99%)] to-[hsl(38_22%_94%)] p-6 sm:p-8"
            data-testid="memorial-keepsakes-hub-card"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="max-w-xl">
                <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">
                  <Frame className="h-3.5 w-3.5" aria-hidden />
                  {MEMORIAL_PHOTO_LINK_NAME}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#5c534a]">{MEMORIAL_KEEPSAKES_HUB_COPY}</p>
              </div>
              <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-full" asChild>
                <a href="#memorial-keepsakes">
                  Learn about keepsakes
                  <ArrowRight className="ml-1 h-3 w-3" />
                </a>
              </Button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-label="Loading memorials" />
          </div>
        ) : (
          <div className="space-y-12">
            <Section
              title="Published memorials"
              description="Shareable tribute pages that are live for family and visitors."
              empty={
                published.length === 0
                  ? {
                      title: 'No memorial pages are live yet',
                      description: 'Publish from a person\'s memorial workspace when the time feels right.',
                    }
                  : null
              }
            >
              <div className="grid gap-4">
                {published.map((item) => (
                  <MemorialCard key={item.person?.id || item.memorial?.id} item={item} />
                ))}
              </div>
            </Section>

            <Section
              title="Draft memorials"
              description="Private workspaces — not visible publicly until you publish."
              empty={
                drafts.length === 0
                  ? {
                      title: 'Create a memorial space when the time is right.',
                      description: 'Open a person\'s profile to begin a private memorial workspace.',
                    }
                  : null
              }
              emptyAction={
                eligible.length > 0 ? (
                  <Button type="button" size="sm" className="rounded-full" asChild>
                    <Link to={memorialManageHref(eligible[0].person)}>
                      Open memorial workspace
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </Button>
                ) : null
              }
            >
              <div className="grid gap-4">
                {drafts.map((item) => (
                  <MemorialCard key={item.person?.id || item.memorial?.id} item={item} />
                ))}
              </div>
            </Section>

            <Section
              title="Eligible for memorial setup"
              description="People in your archive who do not yet have a memorial workspace."
              empty={
                eligible.length === 0
                  ? {
                      title: 'Everyone listed above has a workspace',
                      description: 'When you add new people, they can appear here for memorial setup.',
                    }
                  : null
              }
            >
              <div className="grid gap-4">
                {eligible.slice(0, 20).map((item) => (
                  <MemorialCard key={item.person?.id} item={item} variant="eligible" />
                ))}
              </div>
            </Section>

            <Section
              id="memorial-keepsakes"
              title="Memorial Keepsakes"
              description="Memorial Photo Link — framed photos and tribute prints with NFC or QR. Configure from each memorial workspace after publishing."
            >
              <p className="-mt-2 mb-4 max-w-2xl text-sm font-serif text-[#5c534a]">
                A framed memorial photo that opens a living tribute by tap or scan.
              </p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-xl border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)] p-5">
                  <Frame className="h-8 w-8 text-[#8b7355]/70" strokeWidth={1.25} />
                  <p className="mt-3 font-medium text-foreground/90">Memory Frame 6×6</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    A compact framed photo with hidden NFC and a memorial QR link.
                  </p>
                </div>
                <div className="rounded-xl border border-[hsl(38_14%_86%)] bg-white/80 p-5">
                  <QrCode className="h-8 w-8 text-[#8b7355]/70" strokeWidth={1.25} />
                  <p className="mt-3 font-medium text-foreground/90">A5 Memorial Portrait</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    A premium portrait print for the home, connected to the memorial page.
                  </p>
                </div>
                <div className="rounded-xl border border-[hsl(38_14%_86%)] bg-gradient-to-br from-[hsl(42_28%_99%)] to-[hsl(38_22%_94%)] p-5 sm:col-span-2 lg:col-span-1">
                  <ExternalLink className="h-8 w-8 text-[#8b7355]/70" strokeWidth={1.25} />
                  <p className="mt-3 font-medium text-foreground/90">A4 Legacy Tribute Print</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    A larger tribute print with photo, name, dates and memorial link.
                  </p>
                </div>
              </div>
              {published.length > 0 ? (
                <div className="mt-5">
                  <Button type="button" size="sm" className="rounded-full" asChild>
                    <Link to={memorialManageHref(published[0].person)}>
                      Open keepsakes workspace
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </Button>
                </div>
              ) : null}
            </Section>
          </div>
        )}
      </main>

      <AppFooter variant="light" />
    </div>
  );
}
